import get from 'lodash/get';

import AppViewManager from 'app/AppViewManager';
import RetailManager from 'app/retail_manager/RetailManager';
import BasePaymentController from 'app/src/controllers/payments/BasePaymentController';
import PaymentServiceAlert from 'app/src/controllers/payments/PaymentServiceAlert';
import Decimal from 'app/src/data/base/Decimal';
import CommonData from 'app/src/data/CommonData';
import Format from 'app/src/data/Format';
import CCTerminal from 'app/src/data/models/CCTerminal';
import GatewayConfig from 'app/src/data/models/GatewayConfig';
import Localize from 'app/src/localize/Localize';
import PaymentAnalytics from 'app/src/payments/PaymentAnalytics';
import PaymentFactory from 'app/src/payments/PaymentFactory';
import PaymentOperations from 'app/src/payments/PaymentOperations';
import PaymentProvider from 'app/src/payments/PaymentProvider';
import PaymentTypeManager from 'app/src/payments/PaymentTypeManager';
import SaleCalculationHelper from 'app/src/payments/SaleCalculationHelper';
import SaleManager from 'app/src/payments/SaleManager';
import SwipePayment from 'app/src/payments/SwipePayment';
import Terminal from 'app/src/payments/terminals/Terminal';
import PaymentCreditDebitView from 'app/src/views/payments/PaymentCreditDebitView';
import { createReactRenderer } from 'helpers/react-renderer';
import TransactionResultModal from 'payments/components/TransactionResultModal';
import ManualPayment from 'payments/ManualPayment';
import TerminalPayment from 'payments/TerminalPayment';
import { TipModalWrapper } from 'root/apps/payments/TippingModal';
import Monitoring from 'shared_modules/Monitoring';

let _Events = {
  CREDIT_DEBIT_FINISHED: 'credit:debit:finished',
  SWIPE_PAYMENT_RECEIVED: 'swipe:payment:received',
};

export default class PaymentCreditDebitViewController extends BasePaymentController {
  constructor(saleManager, paymentProvider, options = {}) {
    if (!(paymentProvider instanceof PaymentProvider)) {
      throw 'A payment provider must be provided.';
    }

    super(saleManager);

    this._paymentProvider = paymentProvider;
    this._appViewManager = AppViewManager.getInstance();
    this._supportedFeatures = PaymentProvider.supportedFeaturesForGateway(
      get(this._paymentProvider, 'activeGateway.attributes', {})
    );

    // set options and defaults
    this._options = options;
    this._options.isDepositPayment = this._options.isDepositPayment || false;
    this._options.refundedPayment = this._options.refundedPayment || null;
    this._options.isUnreferencedRefund = this._options.isUnreferencedRefund || false;

    // set the refunedPaymentID if we received a refunedPayment in the options
    if (this._options.refundedPayment) {
      this._refundedPaymentID = get(this._options, 'refundedPayment.serviceModel.id', null);
    }

    // initialize the controller's managed view
    this.view = new PaymentCreditDebitView({
      viewData: {
        currencySymbol: Localize.getCurrencySymbol(),
        saleData: this._saleManager.toJSON(),
        CCTerminals: this._paymentProvider.terminalCollection.toJSON(),
        gateway: this._paymentProvider.activeGateway,
        isDeposit: this._options.isDepositPayment,
        supportsUSBSwipe: this._paymentProvider.activeGatewaySupportsUSBSwipe(),
        showPaymentTerminals:
          this._paymentProvider.activeGatewayHasConfiguredTerminals() ||
          this._paymentProvider.activeGatewaySupportsUSBSwipe(),
        hasSwipePayment: this._hasSwipePayment(),
        showManualEntryButton: !this._options.refundedPayment,
      },
    });

    this.listenTo(
      RetailManager.events,
      RetailManager.Event.SWIPE_DATA_RECEIVED,
      this._onSwipeDataReceived
    );
  }

  /**
   * Export the events via the class
   */
  static get Event() {
    return _Events;
  }

  /**
   * Initializes and displays the Credit/Debit Payment view.
   */
  show() {
    super.show();

    this.listenTo(
      this._saleManager,
      SaleManager.Events.COMPLETE_SALE_FAILED,
      this._onCompleteSaleFailed
    );
  }

  /**
   * Called after the view has been rendered to check if the Gateway has Terminal Support.
   */
  setInitialViewState() {
    let paymentAmount = this._saleManager.pendingBalance; // default the amount to the balance

    if (this._options.refundedPayment && this._supportedFeatures.supportInterac) {
      this.view.disableCreditCardAmountInput();

      const refundedPaymentAmount = this._options.refundedPayment.amount.negated();
      const refundTip = this._options.refundedPayment.refundTip || false;
      // We use the full refundedPaymentAmount available or the pendingBalance if the
      // refundedPaymentAmount is greater than or equal to the paymentAmount
      if (refundedPaymentAmount.isGreaterThanOrEqualTo(paymentAmount)) {
        paymentAmount = refundedPaymentAmount;
      }
      if (refundTip) {
        const tipAmount = this._options.refundedPayment.model.get('tipAmount').abs();
        paymentAmount = paymentAmount.minus(tipAmount);
      }
    } else {
      if (
        this._saleManager.pendingBalance.isZero() &&
        this._paymentProvider.activeGatewayAllowsNegativePaymentRefunds
      ) {
        // if we allow refunds via negated payments then set the amount to negated amount of credit payments
        // currently in the sale
        paymentAmount = this._saleManager.totalCreditPayments.negated();
      }
      // use the specified payment amount rather than the balance if specified
      paymentAmount = this._options.paymentAmount || paymentAmount;
    }

    this.view.setAmount(paymentAmount);

    // If any of the following is true:
    // - We deliberately want to load USB Swipe
    // - The active gateway does not support any known terminals
    // - The active gateway does not support manual entry and there
    //   are no terminals configured (so USB swipe is the only option)
    // AND USB Swipe is supported, then show USB swipe.
    // If it's not supported, something went horribly wrong, so throw an error.

    if (
      !this._paymentProvider.activeGatewaySupportedTerminals ||
      !this._paymentProvider.activeGatewayHasConfiguredTerminals() ||
      this._options.swipeOverride
    ) {
      if (RetailManager.active) {
        RetailManager.cardReader.enableHighPoweredReader(true);
      }

      if (this._paymentProvider.activeGatewaySupportsUSBSwipe()) {
        this.view.showSwipePinPadEntry();
      }
    } else if (CommonData.selectedRegister) {
      let terminalID = CommonData.selectedRegister.get('ccTerminalID');
      this.view.setTerminalSelected(terminalID);
      this._selectTerminal(terminalID);
    }

    // We only hide the manual entry button if it's a refund sale and
    // the manual entry from the terminal is not supported or neither
    // of the manual entry features are available
    if (
      (!this._paymentProvider.activeGatewaySupportsManualEntry &&
        !this._paymentProvider.activeGatewaySupportsManualEntryFromTerminal) ||
      (this._saleManager.isRefundSale() && !this._paymentProvider.supportsManualEntryTerminalRefund)
    )
      this.view.hideManualEntryButton();
  }

  /**
   * Extend the destroy method of the BaseController to make sure we stop polling for
   * terminal status when we leave the view.
   */
  destroy() {
    super.destroy();

    if (RetailManager.active) {
      RetailManager.cardReader.enableHighPoweredReader(false);
    }
  }

  /**
   * Event handler for when the selected terminal has changed
   *
   * @param {Number} terminalID  The ID of the selected terminal
   */
  onSelectedTerminalChanged(terminalID) {
    const shop = window.merchantos?.session?.getShop();
    const register = window.merchantos?.session?.getRegister();
    const employee = window.merchantos?.session?.getEmployee();

    PaymentAnalytics.trackPaymentEvent('Terminal Dropdown', this._saleManager.sale.id, {
      shopId: shop?.shopID || 'None',
      shopName: shop?.name || 'None',
      registerId: register?.registerID || 'None',
      registerName: register?.name || 'None',
      userId: employee?.employeeID || 'None',
      userName: employee ? `${employee.firstName} ${employee.lastName}` : 'None',
      initialTerminalSelected: this._selectedTerminal ? this._selectedTerminal.model.id : 'None',
      initialTerminalName: this._selectedTerminal?.model?.attributes?.name,
      newTerminalSelected: terminalID || 'None',
      newTerminalName: terminalID
        ? this._paymentProvider?.terminalCollection?.models.find(
            terminal => terminal.id === terminalID
          )?.attributes?.name
        : 'None',
      paymentProcessor: this._paymentProvider.activeGatewayProcessorType,
    });

    if (terminalID) {
      // TODO: We don't do anything if this fails; maybe that should change?
      CommonData.selectedRegister.set('ccTerminalID', terminalID).save();
    }

    this._selectTerminal(terminalID);
  }

  /**
   * Event handler for when the cancel button is clicked
   */
  onCreditDebitCancelled() {
    PaymentAnalytics.trackPaymentEvent('Leave', this._saleManager.sale.id, {
      paymentProcessor: this._paymentProvider.activeGatewayProcessorType,
    });

    this.trigger(_Events.CREDIT_DEBIT_FINISHED);
  }

  /**
   * Event handler for when the Start Payment button is clicked.  Starts a terminal payment
   * with the selected terminal.
   *
   * @param {Decimal} amount   The mount to transacted with the terminal.
   * @param {boolean} isRetry  When true, the payment is a retry (used for analytics)
   */
  onStartPaymentClicked(amount, isRetry) {
    let transactionAmount = amount;
    let tipAmount = null;
    // We skip the whole Guard clause in the case of an Interac refund because
    // we already calculated the balance for the merchant and we don't allow
    // them to modify it after
    if (!(this._options.refundedPayment && this._supportedFeatures.supportInterac)) {
      // Guard clause for the case where a refund is being attempted on a gateway
      // that doesn't allow negative payments. This workflow is possible when a gateway
      // has the "allow credits" option.
      if (
        transactionAmount.isNegative() &&
        !this._paymentProvider.activeGateway.allowNegativePayments
      ) {
        PaymentServiceAlert.alert(
          'Refund not permitted. Allow Credits is not enabled in the gateway settings.'
        );
        this.view.enableStartPaymentsButton(true);

        return;
      }

      // only allow negative payments if we're refunding and the gateway allows it
      if (
        transactionAmount.isNegative() &&
        ((!this._saleManager.isRefundSale() &&
          !this._paymentProvider.activeGatewayAllowsNegativePaymentRefunds) ||
          !this._paymentProvider.activeGateway.allowNegativePayments)
      ) {
        transactionAmount = transactionAmount.abs();
        this.view.setAmount(transactionAmount);
      }
    }

    // Limit the amount for negative payment refund enabled gateways to total credit payments
    // currently in the sale
    if (this._paymentProvider.activeGatewayAllowsNegativePaymentRefunds) {
      const totalCreditPayments = SaleCalculationHelper.totalCreditPaymentsInSale(
        this._saleManager
      );
      const totalCreditRefunds = SaleCalculationHelper.totalCreditRefundsInSale(this._saleManager);

      if (
        this._saleManager.isRefundSale() &&
        !amount.isNegative() &&
        totalCreditRefunds.isGreaterThan(0)
      ) {
        transactionAmount = Decimal.min(totalCreditRefunds, amount);
      } else if (
        !this._saleManager.isRefundSale() &&
        amount.isNegative() &&
        totalCreditPayments.isGreaterThan(0)
      ) {
        transactionAmount = Decimal.max(totalCreditPayments.negated(), amount);
      }
      this.view.setAmount(transactionAmount);
    }

    // Restrict the amount to the outstanding balance unless the sale total is zero since deposits
    // may be made on a sale without any line items
    if (
      ((this._saleManager.isRefundSale() && amount.isLessThan(this._saleManager.pendingBalance)) ||
        (!this._saleManager.isRefundSale() &&
          amount.isGreaterThan(this._saleManager.pendingBalance))) &&
      !this._options.isDepositPayment
    ) {
      transactionAmount = this._saleManager.pendingBalance;
      const refundedPayment = this._options.refundedPayment;
      if (refundedPayment && refundedPayment.refundTip) {
        tipAmount = refundedPayment.model
          .get('tipAmount')
          .abs()
          .negated();
        transactionAmount = transactionAmount.plus(tipAmount);
      }
      this.view.setAmount(transactionAmount); // keep the view in sync with the transaction amount
    }

    if (transactionAmount.isZero()) {
      alertUser('The amount cannot be zero.');
      this.view.enableStartPaymentsButton(true);
    } else {
      PaymentAnalytics.trackPaymentEvent('Start/Retry payment', this._saleManager.sale.id, {
        amount: transactionAmount.toString(),
        isRetry,
        paymentProcessor: this._paymentProvider.activeGatewayProcessorType,
      });

      if (this._saleManager.isTipEnabled && !this._saleManager.isRefundSale()) {
        Monitoring.NREvent('tip.start', 'PaymentCreditDebitViewController', {
          description: 'Merchant started the tip flow.',
          transactionAmount: transactionAmount?.toNumber() || null,
          quickPay: false,
        });

        this._startTipFlow({ transactionAmount })
          .then(({ tipAmount, totalAmountWithTip }) => {
            Monitoring.NREvent('tip.complete', 'PaymentCreditDebitViewController', {
              description: 'Merchant completed the tip flow.',
              transactionAmount: transactionAmount?.toNumber() || null,
              tipAmount: tipAmount?.toNumber() || null,
              totalAmountWithTip: totalAmountWithTip?.toNumber() || null,
              quickPay: false,
            });

            this._startTerminalPayment({
              amount: totalAmountWithTip,
              tipAmount,
            });
          })
          .catch(() => {
            Monitoring.NREvent('tip.cancel', 'PaymentCreditDebitViewController', {
              description: 'Merchant cancelled the tip flow.',
              transactionAmount: transactionAmount?.toNumber() || null,
              quickPay: false,
            });

            this.view.enableStartPaymentsButton(true);
          });
      } else {
        this._startTerminalPayment({
          amount: transactionAmount,
          ...(tipAmount && { tipAmount }),
        });
      }
    }
  }

  /**
   * Event handler for when the Cancel button is clicked when a payment is in progress.
   * Attempts to cancel the current transaction.
   */
  onCancelStartedPaymentButtonClicked() {
    this.view.enableCancelTransactionButton(false);
    this._selectedTerminal.cancelTransaction().always(() => {
      this.view.enableCancelTransactionButton(true);
    });
  }

  /**
   * Event handler for when the Void Payment button is clicked after a transaction was
   * approved but had errors.  Attempts to void the transaction.
   */
  onVoidPaymentButtonClicked() {
    if (this._paymentWithErrors) {
      PaymentAnalytics.trackPaymentEvent('Missing signature Void', this._saleManager.sale.id, {
        amount: this._paymentWithErrors.amount.toString(),
        salePaymentID: this._paymentWithErrors.model.id,
        paymentProcessor: this._paymentProvider.activeGatewayProcessorType,
      });
    }

    this._saleManager
      .removePayment(this._paymentWithErrors)
      .always(() => {
        // payment operation is finished
        this.trigger(_Events.CREDIT_DEBIT_FINISHED);
      })
      .fail(() => {
        // TODO: Abstract out alertUser to allow easier re-factoring of how user errors
        // are triggered
        alertUser(Localize.translate('An error occurred while attempting to void the transaction'));
      });
  }

  /**
   * Event handler for when the Accept Payment button is clicked after a transaction was
   * approved but had errors.
   */
  onAcceptPaymentButtonClicked() {
    if (this._paymentWithErrors) {
      PaymentAnalytics.trackPaymentEvent('Missing signature Keep', this._saleManager.sale.id, {
        amount: this._paymentWithErrors.amount.toString(),
        salePaymentID: this._paymentWithErrors.model.id,
        paymentProcessor: this._paymentProvider.activeGatewayProcessorType,
      });
    }

    this.trigger(_Events.CREDIT_DEBIT_FINISHED);
  }

  /**
   * Event handler for when the Process button is clicked after a card has been scanned.
   *
   * @param {string} rawSwipeData     The raw swipe data
   */
  onProcessButtonClicked(rawSwipeData) {
    this._onSwipeDataReceived(rawSwipeData);
  }

  /**
   * Event handler for a keypress in the credit card input field
   *
   * @param {string} rawSwipeData     The raw data from a encrypted card reader
   * @param {event} event             The event that triggered this call
   */
  onCreditInputKeypress(rawSwipeData, event) {
    // Some browsers supper event.which, others support event.keyCode
    var keyPressed = typeof event.which !== 'undefined' ? event.which : event.keyCode;

    // If enter was the last key, submit the data
    if (keyPressed === 13) {
      this._onSwipeDataReceived(rawSwipeData);
    }
  }

  /**
   * Event handler for when the print failed receipt button is clicked.  This button
   * is currently only supported for Cayan.
   */
  onPrintErrorReceiptButtonClicked() {
    if (this._paymentWithErrors) {
      let url =
        `/API/Account/${window.merchantos.session.getSystemCustomerID()}` +
        `/DisplayTemplate/CCCharge/${this._paymentWithErrors.model.get('ccChargeID')}` +
        '.html?template=CCCharge&print=1';

      window.merchantos.print.printUrl({ url, pageType: 'receipt' });
    }
  }

  /**
   * Event handler when the manual entry button is clicked.
   *
   * @param {Customer} customer Unused - The customer associated with the sale
   * @param {Boolean} isDeposit Unused - Whether the amount is for a deposit
   * @param {Decimal} amount  The amount to be transacted
   */
  onManualEntryButtonClicked(_customer, _isDeposit, amount) {
    if (
      this._paymentProvider.activeGatewaySupportsManualEntry ||
      this._paymentProvider.activeGatewaySupportsManualEntryFromTerminal
    ) {
      this._startTerminalPayment({ amount, manualEntry: true });
    }
  }

  /**
   * Manages the logic for selecting a terminal.  Takes care of the UI considerations
   * and managing the currently selected terminal's status.
   *
   * @param {Number} terminalID   The ID of the newly selected terminal
   * @private
   */
  _selectTerminal(terminalID) {
    this._selectedTerminal = this._paymentProvider.getTerminal(terminalID);

    if (!terminalID) {
      // There will be no terminal ID present if the USB option was selected
      if (RetailManager.active) {
        RetailManager.cardReader.enableHighPoweredReader(true);
      }
    }
  }

  _startTipFlow({ transactionAmount }) {
    return TipModalWrapper.startTipFlow({
      terminal: this._selectedTerminal,
      saleManager: this._saleManager,
      transactionAmount,
    });
  }

  /**
   * Performs a transaction with the selected terminal.  When the transaction is finished
   * the appropriate UI action is taken in response.
   *
   * @param {Decimal} amount  The amount to be transacted
   * @param {Decimal} tipAmount  The tipAmount to be transacted (null)
   * @param {Boolean} manualEntry When true manual entry from the terminal will be attempted (false)
   * in card present refund flow
   * @private
   */
  _startTerminalPayment({ amount, tipAmount = null, manualEntry = false }) {
    if (
      [
        GatewayConfig.GatewayType.LIGHTSPEED_PAYMENTS,
        GatewayConfig.GatewayType.VANTIV_TRIPOS,
        GatewayConfig.GatewayType.VANTIV_US,
        GatewayConfig.GatewayType.VANTIV_CANADA,
        GatewayConfig.GatewayType.FIRST_DATA,
      ].includes(this._paymentProvider.activeGateway.get('gatewayID'))
    ) {
      if (manualEntry && this._paymentProvider.activeGatewaySupportsManualEntry) {
        ManualPayment.doManualPayment(amount, this._saleManager, this._paymentProvider);
      } else {
        /* ***************************
         * *** LEGACY CODE WARNING ***
         * ***************************
         * If the current transaction requires a manual payment we check
         * that the gateway support the manual entry on terminal otherwise we leave
         * this action to be picked up by onChildViewManualEntryButtonClicked
         * in the PaymentViewController to attach the right view.
         */
        if (manualEntry && !this._paymentProvider.activeGatewaySupportsManualEntryFromTerminal) {
          return;
        }

        TerminalPayment.doTerminalPayment({
          initialAmount: amount,
          terminal: this._selectedTerminal,
          saleManager: this._saleManager,
          paymentProvider: this._paymentProvider,
          isManualEntry: manualEntry,
          isUnreferencedRefund: this._options.isUnreferencedRefund,
          ...(this._refundedPaymentID && { refundedPaymentID: this._refundedPaymentID }),
          ...(tipAmount && { tipAmount }),
        }).then(() => {
          this.view.enableStartPaymentsButton(true);
        });
      }
    } else {
      /* ***************************
       * *** LEGACY CODE WARNING ***
       * ***************************
       * If the current transaction requires a manual payment we check
       * that the gateway support the manual entry on terminal otherwise we leave
       * this action to be picked up by onChildViewManualEntryButtonClicked
       * in the PaymentViewController to attach the right view.
       */
      if (manualEntry && !this._paymentProvider.activeGatewaySupportsManualEntryFromTerminal) {
        return;
      }

      if (this._selectedTerminal) {
        showNotifier('working');
        PaymentOperations.doPaymentOperation({
          amount,
          terminal: this._selectedTerminal,
          saleManager: this._saleManager,
          paymentProvider: this._paymentProvider,
          isManualEntry: manualEntry,
          ...(tipAmount && { tipAmount }),
        })
          .done(response => {
            switch (response.response) {
              case Terminal.Response.APPROVED:
              case Terminal.Response.APPROVED_WITH_ERROR:
              case Terminal.Response.PAYMENT_ALREADY_RECONCILED:
                if (response.response === Terminal.Response.APPROVED_WITH_ERROR) {
                  // hold onto the payment for later
                  this._paymentWithErrors = response.payment;
                  this.view.showApprovedWithError(response.error);
                } else {
                  this.trigger(_Events.CREDIT_DEBIT_FINISHED);
                }
                break;

              default:
                break;
            }
          })
          .fail((response = {}) => {
            // New fancy print receipt modal workflow
            if (
              response.payment && // this could be null if a Payment was not created (e.g. busy terminal)
              (this._paymentProvider.activeGateway.get('gatewayID') ===
                GatewayConfig.GatewayType.VANTIV_TRIPOS ||
                this._paymentProvider.activeGateway.get('gatewayID') ===
                  GatewayConfig.GatewayType.LIGHTSPEED_PAYMENTS)
            ) {
              const message = get(
                response,
                'payment.serviceModel.paymentTransaction.message',
                response.error
              );

              const detailedMessage = get(
                response,
                'payment.serviceModel.paymentTransaction.parameters.detailedMessage',
                null
              );

              const RenderedComponent = createReactRenderer({
                component: TransactionResultModal,
                selector: '[data-react-root="ReactModal"]',
                props: {
                  onHide: () => {
                    delete this.TransactionResultModal;
                    RenderedComponent.unmount();
                  },
                  onRetryPayment: amount => {
                    this._startTerminalPayment({ amount, manualEntry });
                  },
                  amount,
                  message,
                  detailedMessage,
                  salePaymentID: response.payment.model.get('salePaymentID'),
                  status: response.status,
                  errorCode: get(response, 'payment.serviceModel.errorCode', null),
                  systemCustomerID: window.merchantos.session.getSystemCustomerID(),
                },
              });

              this.TransactionResultModal = RenderedComponent;
              RenderedComponent.mount();
            } else {
              // Old workflow
              // only Cayan supports the print failed receipt button
              let hidePrintReceiptButton =
                this._selectedTerminal.model.get('type') !== CCTerminal.TerminalType.GENIUS;

              this._paymentWithErrors = response.payment;

              switch (response.response) {
                case Terminal.Response.APPROVED:
                case Terminal.Response.APPROVED_WITH_ERROR:
                  alertUser(Localize.translate('Payment not added to the sale.'));
                  this.view.showApprovedWithError(
                    Localize.translate('Payment not added to the sale.')
                  );
                  break;

                case Terminal.Response.CANCELLED_FROM_TERMINAL:
                  // The Conditional check is due to the fact that sometimes the user navigates away
                  // before the view has a chance to load, causing a console error.
                  if (this.view) {
                    this.view.showPaymentError(
                      Localize.translate('Customer cancelled payment'),
                      null,
                      true
                    );
                  }
                  break;

                case Terminal.Response.CANCELLED:
                  this.view.showPaymentError(
                    Localize.translate('Transaction was cancelled'),
                    null,
                    true
                  );
                  break;

                case Terminal.Response.DECLINED:
                  this.view.showPaymentError(
                    Localize.translate('Payment was declined'),
                    null,
                    hidePrintReceiptButton
                  );
                  break;

                case Terminal.Response.TERMINAL_ERROR:
                case Terminal.Response.TIMEOUT:
                  this.view.showPaymentError(
                    Localize.translate('Could not process payment'),
                    response.error,
                    hidePrintReceiptButton
                  );
                  break;

                case Terminal.Response.COULD_NOT_START_TERMINAL:
                case Terminal.Response.ERROR:
                  this.view.showPaymentError(
                    Localize.translate('Could not process payment'),
                    response.error,
                    true
                  );
                  break;

                default:
                  this.view.showPaymentError(
                    Localize.translate('An unknown error has occurred'),
                    null,
                    hidePrintReceiptButton
                  );
                  break;
              }
            }
          })
          .always(() => {
            // during a transaction the start or retry button will be disabled to
            // prevent multiple clicks.  Make sure to re-enable them.
            if (this.view) {
              this.view.enableStartPaymentsButton(true);
              this.view.enableRetryButton(true);
            }

            hideNotifier('working');
          });
      }
    }
  }

  /**
   * Event handler for when swipe data is received from Retail Manager.
   *
   * @param {string} swipeData    TThe card number that was swiped
   * @private
   */
  _onSwipeDataReceived(swipeData) {
    let amount = this.view.getAmount();

    this.view.setProcessButtonDisabled(true);
    window.showNotifier('working');

    // only allow negative payments if we're refunding and the gateway allows it
    if (
      amount.isNegative() &&
      !this._options.isDepositPayment &&
      (!this._saleManager.isRefundSale() ||
        !this._paymentProvider.activeGateway.allowNegativePayments)
    ) {
      amount = amount.negated();
      this.view.setAmount(amount);
    }

    if (this._paymentProvider.activeGatewayHasIntegratedSwipeSupport()) {
      PaymentOperations.doIntegratedSwipeOperation(
        amount,
        this._saleManager,
        this._paymentProvider.activeGateway,
        swipeData
      )
        .done(() => {
          this.trigger(_Events.SWIPE_PAYMENT_RECEIVED);
        })
        .fail(message => {
          this.view.setProcessButtonDisabled(false);
          alertUser(message || Localize.translate('Could not add the payment to the sale.'));
        })
        .always(() => {
          window.hideNotifier('working');
        });
    } else {
      let payment = PaymentFactory.createPayment(PaymentTypeManager.Types.SWIPE, amount, swipeData);
      let promise = null;

      if (this._options.isDepositPayment) {
        // if the payment was for a deposit then add it as such
        promise = this._saleManager
          .addDeposit(
            PaymentFactory.createPayment(
              PaymentTypeManager.Types.DEPOSIT,
              payment,
              this._saleManager.creditAccountID
            )
          )
          .done(() => {
            flashNotifier(
              'success',
              Localize.translate(
                `Credit Account Deposit Of <var>${Format.formatMoney(amount)}</var> Added.`
              ),
              4
            );
          });
      } else {
        promise = this._saleManager.addPayment(payment);
      }

      promise
        .done(() => {
          this.trigger(_Events.SWIPE_PAYMENT_RECEIVED, payment);
        })
        .fail(() => {
          this.view.setProcessButtonDisabled(false);
          alertUser(Localize.translate('Could not add the payment to the sale.'));
        })
        .always(() => {
          window.hideNotifier('working');
        });
    }
  }

  /**
   * Event handler for when the Sale fails
   */
  _onCompleteSaleFailed() {
    this.view.selectCreditCardInput();
  }

  /**
   * Checks to see whether or not a Swipe Payment is pending
   *
   * @returns {bool} ret - Whether or not a Swipe Payment is pending
   */
  _hasSwipePayment() {
    let ret = false;

    _.each(this._saleManager.pendingPayments, currentPayment => {
      if (currentPayment instanceof SwipePayment) {
        ret = true;
      }
    });

    return ret;
  }
}
